import { Link } from 'react-router-dom';
import { ChevronRight, Info } from 'lucide-react';
import ScoreGauge from './ScoreGauge';
import RadarChart from './RadarChart';
import DimensionDetail from './DimensionDetail';
import IssueCard from './IssueCard';
import StrengthCard from './StrengthCard';
import FontRecommendation from './FontRecommendation';
import TrainingPlan from './TrainingPlan';

export default function AnalysisReport({ result, onReset }) {
  const { analysis } = result;
  const hasExtended = !!(analysis.personalityTags || analysis.writingIssues || analysis.trainingPlan);

  return (
    <div className="space-y-6">
      {/* Section 1: Overall Score Gauge */}
      <ScoreGauge
        overallScore={analysis.overallScore}
        level={analysis.level}
        percentileRank={analysis.percentileRank}
        pointsToNextLevel={analysis.pointsToNextLevel}
        personalityTags={analysis.personalityTags}
      />

      {/* Section 2: Radar + Dimension Detail (2-col on desktop) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RadarChart scores={analysis.scores} />
        <DimensionDetail scores={analysis.scores} />
      </div>

      {/* Section 3: Issues (writing + process) */}
      {hasExtended && (analysis.writingIssues?.length > 0 || analysis.processIssues?.length > 0) && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Areas for Improvement</h2>
          <div className="space-y-3">
            {analysis.writingIssues?.map((issue, i) => (
              <IssueCard key={`w-${i}`} issue={issue} type="writing" />
            ))}
            {analysis.processIssues?.map((issue, i) => (
              <IssueCard key={`p-${i}`} issue={issue} type="process" />
            ))}
          </div>
        </div>
      )}

      {/* Section 4: Strengths */}
      {hasExtended && analysis.strengths?.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Your Strengths</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {analysis.strengths.map((s, i) => (
              <StrengthCard key={i} strength={s} />
            ))}
          </div>
        </div>
      )}

      {/* Section 5: Font Recommendations */}
      {hasExtended && analysis.recommendedFonts && (
        <FontRecommendation recommendedFonts={analysis.recommendedFonts} />
      )}

      {/* Section 6: 7-Day Training Plan */}
      {hasExtended && analysis.trainingPlan?.length > 0 && (
        <TrainingPlan trainingPlan={analysis.trainingPlan} analysisId={result.id} />
      )}

      {/* Feedback Summary */}
      {analysis.feedback && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-3">Summary</h2>
          <p className="text-bastion-gray-700 leading-relaxed">{analysis.feedback}</p>
        </div>
      )}

      {/* Backward compat notice for old analyses */}
      {!hasExtended && (
        <div className="card bg-bastion-blue-50 border border-bastion-blue-200">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-bastion-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-bastion-gray-700">
              Upload a new sample for the full 12-point diagnosis report with issues, strengths, font recommendations, and a personalized 7-day training plan.
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button onClick={onReset} className="btn-primary flex-1">
          Analyze Another Sample
        </button>
        <Link to="/tools" className="btn-secondary flex-1 text-center">
          Practice with Worksheets
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
